import { useLocalization } from "@fluent/react";
import { useBreakpoints, Table, Button, Dialog } from "@narmi/design_system";
import { modules } from "byzantine";
import Pagination from "byzantine/src/Pagination";
import { BulkTemplatePaymentRowType } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import PageNavigation from "../../../page_navigation/PageNavigation";
import {
  DIALOG_PAGE_SIZE,
  getDialogWidth,
  formatPaymentAmount,
} from "../../../PaymentActivity/common";
import { SelectedRecipientCard } from "../../../PaymentActivity/CreateBulk/table";
import styles from "../../../PaymentActivity/CreateBulk/table/BulkPaymentTable.module.scss";
import useShowCollectAmounts from "../../../PaymentActivity/CreateBulk/useShowCollectAmounts";

const TemplateReviewPaymentsDialog = ({
  isOpen,
  handleClose,
  payments,
  pageNumber,
  setPageNumber,
}: {
  isOpen: boolean;
  handleClose: () => void;
  payments: BulkTemplatePaymentRowType[];
  pageNumber: number;
  setPageNumber: (page: number) => void;
}) => {
  const { l10n } = useLocalization();
  const { m, l } = useBreakpoints();
  const showCollectAmounts = useShowCollectAmounts(
    payments.some((p) => p.transactionType === "collect")
  );
  const recipients = modules.achPayments.usePermissionedRecipients();

  const pagination = new Pagination({
    total: payments.length,
    page: pageNumber,
    pageSize: DIALOG_PAGE_SIZE,
    navigatePage: (page: number) => {
      setPageNumber(page);
    },
  });

  const startIndex = (pagination.page - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const currentPayments = payments.slice(startIndex, endIndex);

  const COL_VISIBILITY: React.ComponentProps<typeof Table>["colVisibility"] = [
    "*", // recipient
    "*", // action
    "m", // description
    "*", // amount
  ];

  const COL_LAYOUT: React.ComponentProps<typeof Table>["colLayout"] = {
    s: "1fr min-content 1fr",
    m: "2fr 1fr 2fr 1fr",
    l: "2fr 1fr 2fr 1fr",
  };

  return (
    <Dialog
      isOpen={isOpen}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button onClick={handleClose} testId="close-review-template-dialog">
            {l10n.getString("label-close")}
          </Button>
        </div>
      }
      onUserDismiss={handleClose}
      title={l10n.getString("label-template-details")}
      width={getDialogWidth({ m, l })}
    >
      <Table colVisibility={COL_VISIBILITY} colLayout={COL_LAYOUT}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {showCollectAmounts
                ? l10n.getString("label-to-from")
                : l10n.getString("ach-payment-recipient-title")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("label-action")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("transaction-detail-description")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("label-amount")}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {currentPayments.map(
            (payment: BulkTemplatePaymentRowType, index: number) => {
              const { recipient, description, amount, transactionType } =
                payment;

              return (
                <Table.Row key={`table-row__${index}`}>
                  <Table.Cell>
                    {recipient ? (
                      <SelectedRecipientCard
                        recipientId={recipient.id}
                        rowIndex={index}
                        recipients={recipients}
                        staticCard={true}
                      />
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>
                    <span className={styles.capitalize}>{transactionType}</span>
                  </Table.Cell>
                  <Table.Cell>{description}</Table.Cell>
                  <Table.Cell>
                    {formatPaymentAmount({
                      amount,
                      isCollect: transactionType === "collect",
                      showCollectAmounts,
                    })}
                  </Table.Cell>
                </Table.Row>
              );
            }
          )}
        </Table.Body>
      </Table>
      <PageNavigation pagination={pagination} marginClasses="margin--all--l" />
    </Dialog>
  );
};

export default TemplateReviewPaymentsDialog;
